// Supabase client setup for the ADI client portal.
//
// Auth model: password-first with OTP fallback.
// - Sign in (primary): supabase.auth.signInWithPassword({ email, password })
// - Sign in (fallback): supabase.auth.signInWithOtp({ email })  → 6-digit code
// - First-time signup: token_hash from welcome email →
//     verifyOtp({ token_hash, type: 'recovery' }) → updateUser({ password })
// - Forgot password: resetPasswordForEmail({ email }) →
//     verifyOtp({ token_hash, type: 'recovery' }) → updateUser({ password })
//
// All queries respect RLS policies defined in supabase/migrations/20260424000002_rls_policies.sql.
//
// Env vars (from .env.local in dev, Cloudflare Pages env in prod):
// - VITE_SUPABASE_URL
// - VITE_SUPABASE_ANON_KEY

import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { Database, Json } from './database.types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase env vars. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your .env.local',
  );
}

export const supabase: SupabaseClient<Database> = createClient<Database>(
  supabaseUrl,
  supabaseAnonKey,
  {
    auth: {
      // Persist the session across page reloads.
      persistSession: true,
      // Refresh tokens automatically.
      autoRefreshToken: true,
      // Detect a session token in the URL (for OAuth redirects, if added later).
      detectSessionInUrl: false,
      // Use localStorage by default; consider memoryStorage if a "this is a public computer" toggle is added.
      storage: typeof window !== 'undefined' ? window.localStorage : undefined,
      // We use OTP, not magic-link, so flow type is 'pkce' (the modern default for password-less).
      flowType: 'pkce',
    },
  },
);

/** Sign in with email + password (primary path). */
export async function signInWithPassword(email: string, password: string) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email: email.trim().toLowerCase(),
    password,
  });
  if (error) throw error;
  return data;
}

/** Send a password reset email (also used by clients who never set a password). */
export async function requestPasswordReset(email: string) {
  const redirectTo =
    typeof window !== 'undefined' ? `${window.location.origin}/set-password` : undefined;
  const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
    redirectTo,
  });
  if (error) throw error;
}

/**
 * Consume a one-time token_hash from an email link (welcome or password reset).
 * On success, the SDK has a session and the user can call setPassword().
 */
export async function verifyEmailLinkToken(
  tokenHash: string,
  type: 'recovery' | 'invite' | 'magiclink' | 'signup' | 'email_change',
) {
  const { data, error } = await supabase.auth.verifyOtp({
    token_hash: tokenHash,
    type,
  });
  if (error) throw error;
  return data;
}

/** Set/replace the current user's password. Requires an active session. */
export async function setPassword(password: string) {
  const { data, error } = await supabase.auth.updateUser({ password });
  if (error) throw error;
  return data;
}

/** Sign in with an email OTP (fallback path). Sends a 6-digit code. */
export async function requestEmailOtp(email: string) {
  const { error } = await supabase.auth.signInWithOtp({
    email: email.trim().toLowerCase(),
    options: {
      shouldCreateUser: false,
    },
  });
  if (error) throw error;
}

/** Verify the 6-digit code the user typed in. */
export async function verifyEmailOtp(email: string, token: string) {
  const { data, error } = await supabase.auth.verifyOtp({
    email: email.trim().toLowerCase(),
    token: token.trim(),
    type: 'email',
  });
  if (error) throw error;
  return data;
}

/** Sign the user out. */
export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

/** Log a non-table audit event (login, download, etc). Calls the SQL function. */
export async function logAuditEvent(
  action: string,
  meta?: { tableName?: string; rowId?: string; changes?: Record<string, unknown> },
) {
  await supabase.rpc('log_audit_event', {
    p_action: action,
    p_table_name: meta?.tableName,
    p_row_id: meta?.rowId,
    p_changes: meta?.changes ? (meta.changes as Json) : undefined,
    p_user_agent: navigator.userAgent,
  });
}
